<template>
  <div class="accesslink-comment-item px-4 py-2 mt-2" data-cy="accessLink-view-comments-commentItem">
    <div v-if="!showEditForm">

      <div class="pt-2 d-flex justify-space-between align-center">
        <div
          @click="commentClick"
          @keydown.enter.prevent="commentClick"
          @keydown.space.prevent="commentClick"
          @dblclick="commentDblClick"
          role="button"
          tabindex="0"
          class=""
          style="max-width: 45%;"
        >
          <v-tooltip location="bottom" open-delay="200">
            <template v-slot:activator="{ props }">
              <h3 v-bind="props" class="text-truncate">{{ getCreatedBy }}</h3>
            </template>
            <span>{{ getCreatedBy }}</span>
          </v-tooltip>
        </div>

        <div v-if="comment.start !== null" style="max-width: 40%;">
          <div class="d-flex pod-comment-inout-time"
               data-cy="accessLink-view-comments-commentItem-inOutTime">
            <div class="px-2 text-uppercase">
              {{ $t('routes.pod.menuComment.inTime') }}
            </div>
            <div>{{ toTimeMilliseconds(comment.start) }}</div>
            <div v-if="comment.end" class="px-2 text-uppercase">
              {{ $t('routes.pod.menuComment.outTime') }}
            </div>
            <div v-if="comment.end">{{ toTimeMilliseconds(comment.end) }}</div>
          </div>
        </div>

        <div style="max-width: 15%;">
          <v-tooltip location="bottom" open-delay="200">
            <template v-slot:activator="{ props }">
              <div
                v-bind="props"
                @click="commentClick"
                @keydown.enter.prevent="commentClick"
                @keydown.space.prevent="commentClick"
                @dblclick="commentDblClick"
                role="button"
                tabindex="0"
              >
                {{ getTimeDifference }}
              </div>
            </template>
            <span>{{ isoToDatetime(comment.createdat) }}</span>
          </v-tooltip>
        </div>
      </div>

      <div
        class="pod-comment-content"
        :style="commentContentStyle"
        @click="commentClick"
        @keydown.enter.prevent="commentClick"
        @keydown.space.prevent="commentClick"
        @dblclick="commentDblClick"
        role="button"
        tabindex="0"
        data-cy="accessLink-view-comments-commentItem-content"
      >
        {{ comment.content }}
      </div>

      <div class="d-flex align-end flex-column">
        <div>
          <v-small-icon-btn
            icon="mdi-pencil"
            aria-label="Edit comment"
            data-cy="accessLink-view-comments-commentItem-editComment"
            @click="commentDblClick"
          />

          <v-small-icon-btn
            icon="mdi-delete"
            aria-label="Delete comment"
            data-cy="accessLink-view-comments-commentItem-deleteComment"
            @click="commentDelete"
          />
        </div>
      </div>
    </div>

    <AccesslinkCommentsForm
      v-if="showEditForm"
      :pod="pod"
      :comment="comment"
      @visibility="showEditForm = false"
    />
  </div>
</template>

<script>
import { usePlayerStore } from '@/stores/PlayerStore.js'
import { useAccesslinkStore } from '@/stores/AccesslinkStore';
import AccesslinkCommentsForm from './AccesslinkCommentsForm.vue'
import { differenceInYears } from 'date-fns/differenceInYears';
import { differenceInMonths } from 'date-fns/differenceInMonths';
import { differenceInWeeks } from 'date-fns/differenceInWeeks';
import { differenceInDays } from 'date-fns/differenceInDays';
import { differenceInHours } from 'date-fns/differenceInHours';
import { differenceInMinutes } from 'date-fns/differenceInMinutes';
import { toTimeMilliseconds } from '@/components/formatters.js';
import { isoToDatetime } from '@/components/formatters.js';

export default {
  name: "AccesslinkCommentsItem",
  components: {
    AccesslinkCommentsForm,
  },
  props: {
    comment: {
      type: Object,
      required: true,
    },
    pod: {
      type: Object,
      required: true, // not used for now
    }
  },
  data: function() {
    return {
      showEditForm: false,
    };
  },
  methods: {
    isoToDatetime,
    toTimeMilliseconds,
    commentClick: function() {
      const playerStore = usePlayerStore();
      playerStore.setSeekTimeEvent(this.comment.start);
    },
    commentDelete: async function() {
      const accesslinkStore = useAccesslinkStore();
      const recaptchaResponse = await this.$recaptcha('accesslink_comment_delete');

      await accesslinkStore.deleteComment({
        id: this.comment.id,
        recaptchaResponse,
      });
    },
    commentDblClick: function() {
      this.toggleEditForm();
    },
    toggleEditForm: function() {
      this.showEditForm = !this.showEditForm;
    },
  },
  computed: {
    getAccesslinkInfo: function() {
      const accesslinkStore = useAccesslinkStore();
      return accesslinkStore.getAccesslinkInfo;
    },
    getBrandingInfo: function() {
      if (this.getAccesslinkInfo && this.getAccesslinkInfo.brand) {
        return this.getAccesslinkInfo.brand;
      }

      return null;
    },
    playerCurrentTime: function() {
      const playerStore = usePlayerStore();
      return playerStore.currentTime;
    },
    commentContentStyle: function() {
      let color = 'inherit';

      if (this.playerCurrentTime >= this.comment.start &&
          this.playerCurrentTime <= Math.max(this.comment.end, this.comment.start + 1)
      ) {
        if (this.getBrandingInfo && this.getBrandingInfo.color) {
          color = this.getBrandingInfo.color;
        } else {
          color = '#3dabff';
        }
      }

      return { color: color };
    },
    getTimeDifference: function() {
      if (!this.comment) {
        return "?";
      }

      const now = new Date();
      const commentCreatedAt = new Date(this.comment.createdat);

      let years = differenceInYears(now, commentCreatedAt);
      if (years >= 1) {
        return this.$t('common.time.years', years);
      }

      let months = differenceInMonths(now, commentCreatedAt);
      if (months >= 1) {
        return this.$t('common.time.months', months);
      }

      let weeks = differenceInWeeks(now, commentCreatedAt);
      if (weeks >= 1) {
        return this.$t('common.time.weeks', weeks);
      }

      let days = differenceInDays(now, commentCreatedAt);
      if (days >= 1) {
        return this.$t('common.time.days', days);
      }

      let hours = differenceInHours(now, commentCreatedAt);
      if (hours >= 1) {
        return this.$t('common.time.hours', hours);
      }

      return this.$t('common.time.minutes', differenceInMinutes(now, commentCreatedAt));
    },
    getCreatedBy: function() {
      if (this.comment.createdby) {
        return `${this.comment.createdby.firstname} ${this.comment.createdby.lastname}`;
      }

      if (this.comment.email) {
        return this.comment.email;
      }

      return this.$t('common.deletedUser');
    },
  }
}
</script>

<style>
.accesslink-comment-item {
  border-radius: 10px;
  /* font-size: 0.875rem; */
  /* line-height: 1.8; */
  background-color: rgb(var(--v-theme-tw-surface-accent));
}

/* .v-theme--light .accesslink-comment-item {
  background-color: #e6ecf1;
}

.v-theme--dark .accesslink-comment-item {
  background-color: #2f3640;
} */
</style>
